// import camelCase from 'lodash/camelCase';
import { camelCase } from 'change-case';
import isArray from 'lodash/isArray';
import isNull from 'lodash/isNull';
import keys from 'lodash/keys';
import merge from 'lodash/merge';

function wrap(json) {
  if (isArray(json)) {
    return json;
  }

  return [json];
}

function isDate(attributeValue) {
  return Object.prototype.toString.call(attributeValue) === '[object Date]';
}

function camelizeNestedKeys(attributeValue) {
  if (
    attributeValue === null ||
    typeof attributeValue !== 'object' ||
    isDate(attributeValue)
  ) {
    return attributeValue;
  }

  if (isArray(attributeValue)) {
    return attributeValue.map(camelizeNestedKeys);
  }

  const copy = {};

  keys(attributeValue).forEach(k => {
    copy[camelCase(k)] = camelizeNestedKeys(attributeValue[k]);
  });

  return copy;
}

function extractRelationships(
  relationships,
  { camelizeKeys, camelizeTypeValues }
) {
  const ret = {};
  keys(relationships).forEach(key => {
    const relationship = relationships[key];
    const name = camelizeKeys ? camelCase(key) : key;
    ret[name] = {};

    if (typeof relationship.data !== 'undefined') {
      if (isArray(relationship.data)) {
        ret[name].data = relationship.data.map(e => ({
          id: e.id,
          type: camelizeTypeValues ? camelCase(e.type) : e.type,
        }));
      } else if (!isNull(relationship.data)) {
        ret[name].data = {
          id: relationship.data.id,
          type: camelizeTypeValues
            ? camelCase(relationship.data.type)
            : relationship.data.type,
        };
      } else {
        ret[name].data = relationship.data;
      }

      if (typeof relationship.meta !== 'undefined') {
        ret[name].meta = camelizeNestedKeys(relationship.meta);
      }
    }

    if (relationship.links) {
      ret[name].links = camelizeKeys
        ? camelizeNestedKeys(relationship.links)
        : relationship.links;
    }
  });
  return ret;
}

function processMeta(metaObject, { camelizeKeys }) {
  if (camelizeKeys) {
    const meta = {};

    keys(metaObject).forEach(key => {
      meta[camelCase(key)] = camelizeNestedKeys(metaObject[key]);
    });

    return meta;
  }

  return metaObject;
}

function extractEntities(json, { camelizeKeys, camelizeTypeValues }) {
  const ret = {};

  wrap(json).forEach(elem => {
    const type = camelizeKeys ? camelCase(elem.type) : elem.type;

    ret[type] = ret[type] || {};
    ret[type][elem.id] = ret[type][elem.id] || {
      id: elem.id,
    };
    ret[type][elem.id].type = camelizeTypeValues
      ? camelCase(elem.type)
      : elem.type;

    if (camelizeKeys) {
      ret[type][elem.id].attributes = {};

      keys(elem.attributes).forEach(key => {
        ret[type][elem.id].attributes[camelCase(key)] = camelizeNestedKeys(
          elem.attributes[key]
        );
      });
    } else {
      ret[type][elem.id].attributes = elem.attributes;
    }

    if (elem.links) {
      ret[type][elem.id].links = {};

      keys(elem.links).forEach(key => {
        const newKey = camelizeKeys ? camelCase(key) : key;
        ret[type][elem.id].links[newKey] = elem.links[key];
      });
    }

    if (elem.relationships) {
      ret[type][elem.id].relationships = extractRelationships(
        elem.relationships,
        {
          camelizeKeys,
          camelizeTypeValues,
        }
      );
    }

    if (elem.meta) {
      ret[type][elem.id].meta = processMeta(elem.meta, { camelizeKeys });
    }
  });

  return ret;
}

const extractResult = (
  json,
  { camelizeKeys, camelizeTypeValues, resultKey }
) => {
  const ret = { [resultKey]: {} };

  if (isArray(json.data)) {
    ret[resultKey].data = [];
    json.data.forEach(resourceObject => {
      const resourceIdentifierObject = {
        id: resourceObject.id,
        type: camelizeTypeValues
          ? camelCase(resourceObject.type)
          : resourceObject.type,
      };
      ret[resultKey].data.push(resourceIdentifierObject);
    });
  } else {
    const resourceObject = json.data;
    ret[resultKey].data = {
      id: resourceObject.id,
      type: camelizeTypeValues
        ? camelCase(resourceObject.type)
        : resourceObject.type,
    };
  }

  if (json.links) {
    ret[resultKey].links = json.links;
  }

  // if (json.meta) {
  //   ret[resultKey].meta = processMeta(json.meta, { camelizeKeys });
  // }

  if (json.jsonapi) {
    ret[resultKey].jsonapi = json.jsonapi;
  }

  return ret;
};

export default function normalize(json, opts = {}) {
  let {
    camelizeKeys = true,
    camelizeTypeValues = true,
    resultKey = 'result',
    includeOriginalResponse = false,
  } = opts;
  const ret = {};

  if (json.meta) {
    ret.result = { meta: json.meta };
  }

  if (json.errors) {
    ret.errors = json.errors;
  }

  if (json.data) {
    if (includeOriginalResponse) {
      ret.originalResponse = json;
    }

    const result = extractResult(json, {
      camelizeKeys,
      camelizeTypeValues,
      resultKey,
    });
    merge(ret, result);

    const normalizedData = extractEntities(json.data, {
      camelizeKeys,
      camelizeTypeValues,
    });
    merge(ret, normalizedData);

    if (json.included) {
      const normalizedIncluded = extractEntities(json.included, {
        camelizeKeys,
        camelizeTypeValues,
      });
      merge(ret, normalizedIncluded);
    }
  }

  return ret;
}
